import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";

function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <button
      className="theme-toggle"
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
    >
      {theme === "dark" ? "☀️" : "🌙"}
    </button>
  );
}

function FeatureCard({ icon, title, children }) {
  return (
    <div className="feature-card">
      <span className="feature-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function StepCard({ number, title, children }) {
  return (
    <div className="step">
      <div className="step-number">{number}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function Home() {
  const handleInstall = () => {
    ReactGA.event({
      category: "Install",
      action: "Click Install Button",
      label: "Install Bearer My Buddy",
    });
    window.open(process.env.REACT_APP_GS_LINK, "_blank");
  };

  return (
    <main>
      <header>
        <div className="container">
          <ThemeToggle />

          <div className="version-badge">Now on v2.1.0</div>

          <h1>Bearer My Buddy</h1>
          <p className="tagline">
            Auto-detect • Organize • Validate • Copy bearer tokens in seconds.
            <br />
            Stop wasting time in the Network tab — built for devs, testers &
            security folks.
          </p>

          <button className="cta-button" onClick={handleInstall}>
            Add to Chrome – Free
          </button>
        </div>
      </header>

      <section>
        <div className="container">
          <h2>Why You'll Use It Every Day</h2>
          <div className="features">
            <FeatureCard icon="🔑" title="Instant Grab">
              Snags bearer tokens from requests automatically — zero manual
              copy-paste.
            </FeatureCard>

            <FeatureCard icon="🔍" title="Fast Search">
              Filter and find tokens across domains in huge sessions instantly.
            </FeatureCard>

            <FeatureCard icon="✅" title="Validation Badges">
              <span className="status-badge valid">Green = good</span>{" "}
              <span className="status-badge expired">Red = expired</span>.
              <br />
              Know before you try — no more guessing if a token is still alive.
            </FeatureCard>

            <FeatureCard icon="🌐" title="Domain Grouping">
              Tokens neatly bucketed by API origin — multi-service heaven.
            </FeatureCard>

            <FeatureCard icon="⏳" title="Expiration Tracking">
              See TTL/expiry at a glance — no more surprise 401s mid-debug.
            </FeatureCard>

            <FeatureCard icon="🔒" title="Local-Only">
              Zero data leaves your machine — privacy & security first.
            </FeatureCard>

            {/* New features added as cards */}
            <FeatureCard icon="🌙" title="Dark Mode">
              Eye-friendly toggle between light & dark themes — works system-wide or manual.
            </FeatureCard>

            <FeatureCard icon="🔄" title="Auto-Refresh">
              Live updates every 10 seconds — watch tokens appear/expire without clicking.
            </FeatureCard>

            <FeatureCard icon="📋" title="Copy as cURL">
              One-click full cURL command with Bearer header ready for terminal testing.
            </FeatureCard>

            <FeatureCard icon="🛠️" title="Env Vars Export">
              Export tokens as environment variables — perfect for scripts, .env, Postman.
            </FeatureCard>
          </div>
        </div>
      </section>

      <section className="whats-new">
        <div className="container">
          <h2>What's New in v2.1.0</h2>
          <ul className="whats-new-list">
            <li>
              <strong>Dark Mode Toggle</strong> — Switch between light/dark themes instantly (persists via localStorage).
            </li>
            <li>
              <strong>Auto-refresh (every 10s)</strong> — Keep the token list live without manual refresh.
            </li>
            <li>
              <strong>Copy as cURL</strong> — Generate ready-to-run cURL commands with the selected Bearer token.
            </li>
            <li>
              <strong>Export to Env Vars</strong> — Quick copy in export TOKEN=... format for your scripts/CLI.
            </li>
            <li>
              <strong>Improved Filters</strong> — New dropdowns for Valid/Expired + domain sorting.
            </li>
            <li>UI polish: clearer badges, better search, truncated tokens with full copy on hover/click.</li>
          </ul>
          <p className="whats-new-note">
            These updates make debugging APIs faster and more comfortable — especially during long sessions.
          </p>
        </div>
      </section>

      <section className="how-it-works">
        <div className="container">
          <h2>How It Works</h2>
          <div className="steps">
            <StepCard number="1" title="Install">
              Add the extension and allow network access (standard permission).
            </StepCard>

            <StepCard number="2" title="Browse">
              Use your sites normally — it watches quietly in the background.
            </StepCard>

            <StepCard number="3" title="Popup">
              Click the icon → clean, searchable token list ready to copy (now with cURL & env exports!).
            </StepCard>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-top">
            <p className="footer-brand">Bearer My Buddy v2.1.0</p>

            <nav className="footer-links">
              <a href="/support">FAQ / Support</a>
              <a href="mailto:developer@pedrohma.com">Contact</a>
            </nav>
          </div>

          <div className="footer-bottom">
            <p className="footer-heart">Built with ❤️ for API warriors</p>
            <p className="footer-copyright">
              © {new Date().getFullYear()} Pedro Arthur • All data stays local
            </p>
            <p className="footer-location">Made in California, USA</p>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default Home;